import React, { useState } from 'react';
import { Landmark, UserRound, ArrowDownLeft, ArrowUpRight, Activity, Settings2, TrendingUp, TrendingDown, Pencil, Loader2, Trash2 } from 'lucide-react';
import { CatIcon, categoryVisual, curSym } from './pocketIcons';
import { financeApi } from '../../lib/api';
import { toast } from 'sonner';

export const fmtMoney = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
// Currency-aware: shows the symbol the user actually spoke (shekel → ₪, dollar → $ …)
export const fmtCur = (n, currency) => {
  const v = (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
  const c = (currency || 'USD').toUpperCase();
  return c === 'USD' ? `$${v}` : `${v} ${curSym(c)}`;
};
export const relTime = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'الآن';
  if (diff < 3600) return `منذ ${Math.floor(diff / 60)} د`;
  if (diff < 86400) return `منذ ${Math.floor(diff / 3600)} س`;
  return `منذ ${Math.floor(diff / 86400)} يوم`;
};

export const TxRow = ({ t: t0, meta }) => {
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [t, setT] = useState(t0);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isIncome = t.type === 'income';
  const vis = categoryVisual(t);

  // Hide the bottom dock while the detail sheet is open so it can never
  // sit above the sheet buttons (stacking-context quirk).
  React.useEffect(() => {
    if (open || confirmDel) document.body.classList.add('pk-sheet-open');
    else document.body.classList.remove('pk-sheet-open');
    return () => document.body.classList.remove('pk-sheet-open');
  }, [open, confirmDel]);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await financeApi.deleteTransaction(t.id);
      setConfirmDel(false);
      setOpen(false);
      window.dispatchEvent(new Event('pocket-reload'));
      toast.success('تم حذف الحركة ✓', {
        duration: 5000,
        action: {
          label: 'تراجع ↩',
          onClick: async () => {
            try {
              await financeApi.restoreTransaction(t.id);
              toast.success('رجعت الحركة مثل ما كانت ✓');
              window.dispatchEvent(new Event('pocket-reload'));
            } catch (err) { toast.error(err?.response?.data?.detail || 'فشل الاسترجاع'); }
          },
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الحذف');
    } finally { setDeleting(false); }
  };

  const startEdit = () => {
    setDraft({ amount: String(t.amount ?? ''), category: t.category || '', description: t.description || '' });
    setEdit(true);
  };
  const saveEdit = async () => {
    const amount = parseFloat(draft.amount);
    if (!amount || amount <= 0) { toast.error('أدخل مبلغًا صحيحًا'); return; }
    setSaving(true);
    try {
      const r = await financeApi.updateTransaction(t.id, {
        amount, category: draft.category.trim(), description: draft.description.trim(),
      });
      setT(r.data || { ...t, amount, category: draft.category, description: draft.description });
      setEdit(false);
      toast.success('تم حفظ التعديل ✓');
      window.dispatchEvent(new Event('pocket-reload'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل التعديل');
    } finally { setSaving(false); }
  };
  return (
    <>
      <div className={`pk-tx ${isIncome ? 'income' : 'expense'}`} data-testid={`pocket-tx-${t.id}`}
        onClick={() => setOpen(true)} style={{ cursor: 'pointer' }} role="button">
        <div className="pk-tx-ic" style={isIncome ? undefined : { color: vis.color, background: `${vis.color}1f` }}>
          {isIncome ? <ArrowDownLeft size={19} /> : <CatIcon icon={vis.icon} size={18} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="pk-tx-t">{t.category || (isIncome ? 'دخل' : 'مصروف')}</div>
          <div className="pk-tx-m">{meta}</div>
        </div>
        <div className="pk-tx-amt"><span dir="ltr">{isIncome ? '+' : '−'}{fmtCur(t.amount, t.currency)}</span></div>
        <button className="pk-tx-del" data-testid={`pocket-tx-del-${t.id}`} title="حذف"
          onClick={(e) => { e.stopPropagation(); setConfirmDel(true); }}>
          <Trash2 size={15} />
        </button>
      </div>
      {confirmDel && (
        <>
          <div className="pk-backdrop" onClick={() => setConfirmDel(false)} style={{ zIndex: 96 }} />
          <div className="pk-sheet" style={{ zIndex: 97 }} data-testid={`pocket-tx-del-confirm-${t.id}`}>
            <div className="pk-grip" />
            <div style={{ textAlign: 'center', padding: '6px 0 2px' }}>
              <Trash2 size={38} style={{ color: 'var(--pk-red)' }} />
            </div>
            <div className="pk-sheet-title" style={{ textAlign: 'center' }}>حذف هذه الحركة؟</div>
            <div className="pk-sheet-sub" style={{ textAlign: 'center' }} dir="auto">
              {t.category || (isIncome ? 'دخل' : 'مصروف')} — <span dir="ltr">{fmtMoney(t.amount_usd || t.amount)}</span>
              <br />الحذف نهائي ولا يمكن التراجع عنه
            </div>
            <button className="pk-btn danger" style={{ marginTop: 16 }} onClick={doDelete} disabled={deleting} data-testid="pocket-tx-del-confirm-btn">
              {deleting ? <Loader2 size={15} className="animate-spin" /> : 'نعم، احذف نهائيًا'}
            </button>
            <button className="pk-btn ghost" onClick={() => setConfirmDel(false)} data-testid="pocket-tx-del-cancel-btn">إلغاء</button>
          </div>
        </>
      )}
      {open && (
        <>
          <div className="pk-backdrop" onClick={() => { setOpen(false); setEdit(false); }} style={{ zIndex: 96 }} />
          <div className="pk-sheet" style={{ zIndex: 97 }} data-testid={`pocket-tx-detail-${t.id}`}>
            <div className="pk-grip" />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div className="pk-tx-ic" style={{ width: 40, height: 40, ...(isIncome ? {} : { color: vis.color, background: `${vis.color}1f` }) }}>
                {isIncome ? <ArrowDownLeft size={20} /> : <CatIcon icon={vis.icon} size={19} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 900 }} dir="auto">{t.category || (isIncome ? 'دخل' : 'مصروف')}</div>
                <div style={{ fontSize: 11.5, color: 'var(--pk-mut)', marginTop: 2 }} dir="ltr">
                  {new Date(t.created_at || t.date).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
                  {' · '}{isIncome ? 'دخل' : t.wallet === 'reserve' ? 'Reserve' : 'Personal'}
                  {t.person ? ` · ${t.person}` : ''}
                </div>
              </div>
              <div style={{ fontSize: 19, fontWeight: 800, color: isIncome ? '#34D399' : 'var(--pk-gold-light)' }} dir="ltr" className="pk-num">
                {isIncome ? '+' : '−'}{fmtCur(t.amount, t.currency)}
              </div>
            </div>
            {t.currency && t.currency !== 'USD' && (
              <div style={{ fontSize: 12, color: 'var(--pk-mut)', marginTop: 8 }} dir="ltr">
                {t.amount} {t.currency} → {fmtMoney(t.amount_usd)}
              </div>
            )}
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.16em', color: 'var(--pk-gold)', textTransform: 'uppercase' }} className="pk-latin">Details · التفاصيل</div>
              {edit ? (
                <div style={{ marginTop: 8 }}>
                  <input type="text" className="pk-input" value={draft.category}
                    onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
                    placeholder="العنوان" data-testid="tx-edit-category" />
                  <input type="number" inputMode="decimal" className="pk-input" value={draft.amount} dir="ltr"
                    onChange={(e) => setDraft((d) => ({ ...d, amount: e.target.value }))}
                    placeholder={`المبلغ (${t.currency || 'USD'})`} style={{ marginTop: 8 }} data-testid="tx-edit-amount" />
                  <textarea className="pk-input" rows={4} value={draft.description}
                    onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                    placeholder="الوصف الكامل…" style={{ marginTop: 8, resize: 'none', lineHeight: 1.8 }} data-testid="tx-edit-description" />
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button className="pk-btn" style={{ flex: 1 }} onClick={saveEdit} disabled={saving} data-testid="tx-edit-save">
                      {saving ? <Loader2 size={15} className="animate-spin" /> : 'حفظ التعديل ✓'}
                    </button>
                    <button className="pk-btn ghost" style={{ flex: 1 }} onClick={() => setEdit(false)} data-testid="tx-edit-cancel">إلغاء</button>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: 6, padding: '12px 14px', borderRadius: 13, background: 'rgba(0,0,0,0.35)', border: '1px solid var(--pk-line)', fontSize: 13.5, lineHeight: 1.9, whiteSpace: 'pre-wrap' }} dir="auto" data-testid="pocket-tx-description">
                  {t.description?.trim() || 'لا يوجد وصف مسجل لهذه الحركة'}
                </div>
              )}
            </div>
            {!edit && (
              <>
                <div style={{ display: 'flex', gap: 14, marginTop: 12, fontSize: 11.5, color: 'var(--pk-mut)' }}>
                  <span>المصدر: {t.source === 'pocket' ? '🎙 صوتي' : t.source || 'يدوي'}</span>
                  {t.created_by && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} dir="ltr">{t.created_by}</span>}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button className="pk-btn" style={{ flex: 1 }} onClick={startEdit} data-testid="pocket-tx-edit-btn">
                    <Pencil size={14} style={{ verticalAlign: '-2px' }} /> تعديل
                  </button>
                  <button className="pk-btn danger" style={{ flex: 1 }} onClick={() => { setOpen(false); setConfirmDel(true); }} data-testid="pocket-tx-detail-del-btn">
                    <Trash2 size={14} style={{ verticalAlign: '-2px' }} /> حذف
                  </button>
                  <button className="pk-btn ghost" style={{ flex: 1 }} onClick={() => setOpen(false)} data-testid="pocket-tx-detail-close">إغلاق</button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};

const _dayKey = (t) => {
  const d = new Date(t.date || t.created_at);
  return isNaN(d) ? 'unknown' : d.toISOString().slice(0, 10);
};
const _dayLabel = (key) => {
  if (key === 'unknown') return 'بدون تاريخ';
  const today = new Date().toISOString().slice(0, 10);
  const y = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  if (key === today) return 'اليوم';
  if (key === y) return 'أمس';
  return new Date(key).toLocaleDateString('ar', { weekday: 'long', day: 'numeric', month: 'long' });
};

// Groups transactions by calendar day with a separator header + a running
// per-day total footer (overall spend for that day, in USD across currencies).
export const DayGroups = ({ items, metaFor }) => {
  const groups = {};
  const order = [];
  (items || []).forEach((t) => {
    const k = _dayKey(t);
    if (!groups[k]) { groups[k] = []; order.push(k); }
    groups[k].push(t);
  });
  return order.map((k) => {
    const list = groups[k];
    const spent = list.filter((t) => t.type !== 'income').reduce((s, t) => s + (Number(t.amount_usd ?? t.amount) || 0), 0);
    const income = list.filter((t) => t.type === 'income').reduce((s, t) => s + (Number(t.amount_usd ?? t.amount) || 0), 0);
    return (
      <div key={k} data-testid={`pocket-day-${k}`} style={{ marginTop: 6 }}>
        <div className="pk-dayhead">
          <span className="lbl">{_dayLabel(k)}</span>
          <span className="rule" />
          <span className="cnt" dir="ltr">{list.length} حركة</span>
        </div>
        {list.map((t) => (
          <TxRow key={t.id} t={t}
            meta={metaFor ? metaFor(t) : `${relTime(t.date || t.created_at)} · ${t.type === 'income' ? 'دخل' : t.wallet === 'reserve' ? 'Reserve' : 'Personal'}`} />
        ))}
        <div className="pk-daytotal" data-testid={`pocket-day-total-${k}`}>
          <span className="lbl">إجمالي اليوم</span>
          <span className="val" dir="ltr">
            {income > 0 && <span style={{ color: '#34D399' }}>+{fmtMoney(income)}</span>}
            <span style={{ color: 'var(--pk-gold-light)' }}>−{fmtMoney(spent)}</span>
          </span>
        </div>
      </div>
    );
  });
};

export const PocketHome = ({ user, wallets, recent, insights, categories, online, queueCount, onQuickTap, onManageCats, onGoInsights }) => {
  const name = user?.name || user?.email?.split('@')[0] || 'You';
  const d = insights?.delta_pct;
  return (
    <div className="pk-view" data-testid="pocket-home-view">
      <div className="pk-topbar">
        <div>
          <div className="pk-greet">Overview</div>
          <div className="pk-uname">أهلًا، {name}</div>
        </div>
        <div className="pk-statusrow">
          {online
            ? <span className="pk-pill online" data-testid="pocket-online-pill"><span className="pk-dot live" /> LIVE</span>
            : <span className="pk-pill offline" data-testid="pocket-offline-pill"><span className="pk-dot" /> OFFLINE{queueCount > 0 ? ` · ${queueCount}` : ''}</span>}
          <div className="pk-avatar">{(name[0] || 'P').toUpperCase()}</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '0 20px' }}>
        <div className="pk-card gold" style={{ padding: '16px 16px 17px' }} data-testid="pocket-balance-personal">
          <div className="pk-card-orb" />
          <div className="pk-scan" />
          <div className="pk-card-tag"><UserRound size={12} /> Personal</div>
          <div className="pk-balval"><span dir="ltr">{fmtMoney(wallets?.personal?.balance)}</span></div>
          <div className="pk-balsub">محفظتي الشخصية · {100 - Math.round((wallets?.reserve_percentage || 0.2) * 100)}%</div>
        </div>
        <div className="pk-card mag" style={{ padding: '16px 16px 17px' }} data-testid="pocket-balance-reserve">
          <div className="pk-card-orb" />
          <div className="pk-scan" />
          <div className="pk-card-tag"><Landmark size={12} /> Reserve</div>
          <div className="pk-balval"><span dir="ltr">{fmtMoney(wallets?.reserve?.balance)}</span></div>
          <div className="pk-balsub">احتياطي الشغل · {Math.round((wallets?.reserve_percentage || 0.2) * 100)}%</div>
        </div>
      </div>

      {insights && (
        <div style={{ padding: '12px 20px 0' }}>
          <button onClick={onGoInsights} className="pk-card" data-testid="pocket-insight-strip"
            style={{ width: '100%', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 13, cursor: 'pointer', textAlign: 'inherit', fontFamily: 'inherit', color: 'inherit' }}>
            <span style={{ width: 40, height: 40, borderRadius: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.28)', color: 'var(--pk-gold)', flexShrink: 0 }}>
              <Activity size={19} />
            </span>
            <div style={{ flex: 1 }}>
              <div className="pk-card-tag">Smart insights · هذا الأسبوع</div>
              <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>
                صرفت <span dir="ltr" className="pk-num pk-gold-text" style={{ fontWeight: 800 }}>{fmtMoney(insights.week_total)}</span>
                {d != null && (
                  <span className={`pk-delta ${d >= 0 ? 'up' : 'down'}`} style={{ marginInlineStart: 8 }}>
                    {d >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                    <span dir="ltr">{Math.abs(d)}%</span> {d >= 0 ? 'أعلى' : 'أقل'} من متوسطك
                  </span>
                )}
              </div>
            </div>
          </button>
        </div>
      )}

      <div className="pk-sect">
        تسجيل سريع
        <button className="pk-sect-action" onClick={onManageCats} data-testid="pocket-manage-cats-btn"><Settings2 size={13} style={{ verticalAlign: '-2px' }} /> تخصيص</button>
      </div>
      <div className="pk-chips" data-testid="pocket-quick-chips">
        {(categories || []).map((c, i) => {
          const vis = categoryVisual(c);
          return (
            <button key={i} className="pk-qchip" onClick={() => onQuickTap(c)} data-testid={`pocket-qchip-${i}`}>
              <span className="ic" style={{ color: vis.color, background: `${vis.color}1a`, borderColor: `${vis.color}3d` }}><CatIcon icon={c.icon} size={19} /></span>
              {c.name}
            </button>
          );
        })}
      </div>

      <div className="pk-sect">آخر الحركات</div>
      <DayGroups items={(recent || []).slice(0, 40)} />
      {(!recent || recent.length === 0) && <div className="pk-empty">لا حركات بعد — اضغط زر المايك وابدأ</div>}
    </div>
  );
};
